import { useEffect, useCallback, useState } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Box, CircularProgress } from '@mui/material';
import api from '@/services/api';

// API scope for the Function App
const apiScope = import.meta.env.VITE_API_SCOPE || 
  `api://${import.meta.env.VITE_AZURE_CLIENT_ID}/access_as_user`;

interface ApiProviderProps {
  children: React.ReactNode;
}

export default function ApiProvider({ children }: ApiProviderProps) {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || null);
  const [isTokenReady, setIsTokenReady] = useState(false);

  const acquireToken = useCallback(async () => {
    if (!account) {
      api.setAccessToken(null);
      setIsTokenReady(false);
      return;
    }

    try {
      const response = await instance.acquireTokenSilent({
        scopes: [apiScope],
        account: account,
      });
      api.setAccessToken(response.accessToken);
      setIsTokenReady(true);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired or needs interaction - redirect to login
        try {
          await instance.acquireTokenRedirect({
            scopes: [apiScope],
            account: account,
          });
        } catch (redirectError) {
          console.error('Failed to acquire token via redirect:', redirectError);
          api.setAccessToken(null);
          setIsTokenReady(false);
        }
      } else {
        console.error('Failed to acquire token:', error);
        api.setAccessToken(null);
        setIsTokenReady(false);
      }
    }
  }, [instance, account]);

  // Acquire token on mount and when account changes
  useEffect(() => {
    acquireToken();
  }, [acquireToken]);

  // Refresh token periodically (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(acquireToken, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [acquireToken]);

  // Wait for token before rendering children
  if (!isTokenReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
}
